import * as React from 'react';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import TitledContainer from '../../components/TitledContainer';
import { getCashRegisterEntriesAsync, getUsersAsync } from '../../ajax/requests';
import { CashRegisterEntryData } from '../../models';
import CashRegisterProcessModal from '../../components/CashRegister/CashRegisterProcessModal';
import { pendingBalanceOf } from '../../shared/appointmint/cashRegister';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { UserEntity } from '../../store/entities/state';
import { loadEntities } from '../../store/entities/actions';
import { Card, CardHeader, Badge, Alert } from 'reactstrap';
import { ComboField } from '../../components/Forms/fields/ComboField';
import Typography from '../../components/Typography';
import moment from 'moment';
import { formatEuros } from '../../shared/string';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { isAdministrator, getMyUser } from '../../store/session/selectors';
import { getSettings } from '../../store/settings/selectors';
import { loadSettings } from '../../store/settings/actions';
import { Selectors as GeneralSelectors, Actions as GeneralActions } from '../../store/general';
import SettingsIcon from '@material-ui/icons/Settings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import IconButton from '@material-ui/core/IconButton';
import { push } from 'connected-react-router';
import { paths } from '../../app/constants';

interface GroupedEntries {
    key: string;
    username?: string;
    cashRegisterName?: string;
    pendingByPaymentType: Record<string, number>;
    pendingTotal: number;
    hasActionablePending: boolean;
}

interface ManagedRegister {
    username?: string;
    cashRegisterName?: string;
    displayName: string;
    paymentType: string;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const UserBoxesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacer-M);
`;

const UserBox = styled(Card)<{ children?: React.ReactNode }>`
    min-width: 250px;
    flex: 1;
`;

const UserBoxHeader = styled(CardHeader)`
    display: flex;
    justify-content: space-between;
    gap: var(--spacer-S);
    align-items: center;
`;

const TypeRow = styled.div<{ $last: boolean; onClick?: React.MouseEventHandler<HTMLDivElement>; children?: React.ReactNode }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacer-XS);
    border-bottom: ${p => p.$last ? 'none' : 'var(--border-thin) solid var(--appLigthGrey)'};
    cursor: pointer;
    border-radius: 0.3rem;
    transition: background 0.15s ease;

    &:hover {
        background: var(--bg-hover-light);
    }
`;

const TypeRowRight = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
`;

const OpenRowButton = styled(IconButton)`
    && {
        padding: var(--spacer-XXS);
        border-radius: 0.35rem;
        background-color: var(--brand-secondary);
        color: var(--brand-accent);
        flex-shrink: 0;

        svg {
            font-size: 0.95rem;
        }

        &:hover {
            background-color: var(--brand-secondary);
            opacity: 0.85;
        }
    }
`;

const SummaryRow = styled.div`
    display: flex;
    gap: var(--spacer-M);
    flex-wrap: wrap;
    margin-bottom: var(--spacer-S);
`;

const SummaryItem = styled.div<{ children?: React.ReactNode }>`
    padding: var(--spacer-S);
    background-color: var(--bg-light);
    border-radius: 4px;
    min-width: 150px;
`;

const SummaryLabel = styled.div`
    font-size: smaller;
    color: var(--text-muted);
`;

const SummaryValue = styled.div`
    font-size: large;
    font-weight: bold;
`;

const ErrorText = styled.span`
    color: var(--danger-color);
`;

const CashRegister: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const users = useSelector(getEntitiesDataItems<UserEntity>('users'));
    const isAdmin = useSelector(isAdministrator);
    const currentUser = useSelector(getMyUser);
    const settings = useSelector(getSettings);
    const [entries, setEntries] = React.useState<CashRegisterEntryData[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [managedRegister, setManagedRegister] = React.useState<ManagedRegister | null>(null);
    const selectedCashRegisterName = useSelector(GeneralSelectors.getSelectedCashRegisterName);
    const sharedRegisterNames = settings.cashRegisterSettings?.sharedRegisterNames ?? [];

    const canApprove = React.useMemo(() => {
        if (isAdmin) return true;
        const nonAdminApprovers = settings.cashRegisterSettings?.nonAdminApprovers || [];
        return currentUser?.username ? nonAdminApprovers.includes(currentUser.username) : false;
    }, [isAdmin, settings.cashRegisterSettings?.nonAdminApprovers, currentUser?.username]);

    const usernameToName = React.useMemo(() => {
        const map: Record<string, string> = {};
        users.forEach(user => {
            map[user.username] = user.name || user.username;
        });
        return map;
    }, [users]);

    const getGroupDisplayName = React.useCallback((group: GroupedEntries) =>
        group.cashRegisterName ?? (group.username ? (usernameToName[group.username] || group.username) : group.key),
        [usernameToName]);

    const loadEntries = React.useCallback(() => {
        const from = moment().subtract(90, 'days').startOf('day').format('YYYY-MM-DDTHH:mm:ss');
        const to = moment().endOf('day').format('YYYY-MM-DDTHH:mm:ss');
        return getCashRegisterEntriesAsync(from, to).then(data => setEntries(data));
    }, []);

    React.useEffect(() => {
        dispatch(loadSettings());
        isAdmin && dispatch(loadEntities('users', () => getUsersAsync(), 'NewResult'));

        loadEntries()
            .catch(err => {
                console.error('Failed to load cash register entries:', err);
                setError(err.message || 'Failed to load data');
            })
            .finally(() => setIsLoading(false));
    }, []);

    const groupedByUsername = React.useMemo(() => {
        const grouped: Record<string, CashRegisterEntryData[]> = {};
        const registerKeys = new Set<string>();

        entries.forEach(entry => {
            const key = entry.cashRegisterName || entry.username || 'Unknown';
            if (entry.cashRegisterName) registerKeys.add(key);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(entry);
        });

        const result: GroupedEntries[] = Object.entries(grouped).map(([key, groupEntries]) => {
            // Seed every payment type that ever appeared so fully-processed types
            // stay visible (at 0) for manual operations and history.
            const seenTypes = Array.from(new Set(groupEntries.map(e => e.paymentType)));
            const pendingByPaymentType: Record<string, number> = Object.fromEntries(
                seenTypes.map(type => [type, pendingBalanceOf(groupEntries, type)]),
            );

            return {
                key,
                username: registerKeys.has(key) ? undefined : key,
                cashRegisterName: registerKeys.has(key) ? key : undefined,
                pendingByPaymentType,
                pendingTotal: Object.values(pendingByPaymentType).reduce((a, b) => a + b, 0),
                hasActionablePending: groupEntries.some(e => !e.handledByUser),
            };
        });

        return result.sort((a, b) => b.pendingTotal - a.pendingTotal);
    }, [entries]);

    const pendingPerMethod = React.useMemo(() => {
        // Seed every payment type that ever appeared so methods stay visible
        // (at 0) after the register is fully processed.
        const totals: Record<string, number> = {};
        entries.forEach(e => { totals[e.paymentType] = totals[e.paymentType] ?? 0; });
        entries.filter(e => !e.handledByUser).forEach(e => {
            totals[e.paymentType] += e.paymentValue;
        });
        return totals;
    }, [entries]);

    const goToCashRegisterSettings = React.useCallback(() => {
        dispatch(push(`${paths.settings()}/cash-register`));
    }, [dispatch]);

    const openRegister = React.useCallback((group: GroupedEntries, paymentType: string) => {
        setManagedRegister({
            username: group.username,
            cashRegisterName: group.cashRegisterName,
            displayName: getGroupDisplayName(group),
            paymentType,
        });
    }, [getGroupDisplayName]);

    const renderGroupGrid = (groups: GroupedEntries[]) => {
        if (groups.length === 0) return null;
        return (
            <UserBoxesGrid>
                {groups.map(group => {
                    const types = Object.entries(group.pendingByPaymentType);
                    return (
                        <div key={group.key} style={{ minWidth: '250px' }}>
                            <UserBox>
                                <UserBoxHeader>
                                    <strong>{getGroupDisplayName(group)}</strong>
                                    {group.hasActionablePending ? (
                                        <Badge color="warning">{formatEuros(group.pendingTotal)}</Badge>
                                    ) : group.pendingTotal > 0 ? (
                                        <Badge color="info">{formatEuros(group.pendingTotal)}</Badge>
                                    ) : (
                                        <Badge color="success">{t(TK.cashRegisterNoPendingEntries)}</Badge>
                                    )}
                                </UserBoxHeader>
                                {types.length > 0 && (
                                    <div style={{ margin: 'var(--spacer-XS) var(--spacer-S)' }}>
                                        {types.map(([type, value], i) => (
                                            <TypeRow
                                                key={type}
                                                $last={i === types.length - 1}
                                                onClick={() => openRegister(group, type)}
                                            >
                                                <span>{type}</span>
                                                <TypeRowRight>
                                                    <strong>{formatEuros(value)}</strong>
                                                    <OpenRowButton size="small" tabIndex={-1}>
                                                        <ArrowForwardIcon fontSize="small" />
                                                    </OpenRowButton>
                                                </TypeRowRight>
                                            </TypeRow>
                                        ))}
                                    </div>
                                )}
                            </UserBox>
                        </div>
                    );
                })}
            </UserBoxesGrid>
        );
    };

    if (isLoading) {
        return (
            <TitledContainer id="cash-register" title={t(TK.cashRegister)}>
                <Typography>{t(TK.loading)}...</Typography>
            </TitledContainer>
        );
    }

    if (error) {
        return (
            <TitledContainer id="cash-register" title={t(TK.cashRegister)}>
                <ErrorText>{error}</ErrorText>
            </TitledContainer>
        );
    }

    const sharedGroups = groupedByUsername.filter(g => !!g.cashRegisterName);
    const personalGroups = groupedByUsername.filter(g => !g.cashRegisterName);

    return (
        <TitledContainer
            id="cash-register"
            title={t(TK.cashRegister)}
            extraActions={isAdmin ? (
                <IconButton size="small" onClick={goToCashRegisterSettings} title={t(TK.settings)}>
                    <SettingsIcon fontSize="small" />
                </IconButton>
            ) : undefined}
        >
            <Container>
                <Alert color="secondary" style={{ margin: 0 }}>
                    {t(TK.cashRegisterPageInfo)}
                </Alert>
                {sharedRegisterNames.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ width: 220 }}>
                            <ComboField
                                label={t(TK.cashRegisterCurrentRegister)}
                                value={selectedCashRegisterName ?? ''}
                                options={[
                                    { value: '', label: t(TK.cashRegisterOwnRegister) },
                                    ...sharedRegisterNames.map(name => ({ value: name, label: name })),
                                ]}
                                onChange={value => dispatch(GeneralActions.selectCashRegisterName(value ?? ''))}
                            />
                        </div>
                    </div>
                )}
                <SummaryRow>
                    {Object.entries(pendingPerMethod).map(([method, total]) => (
                        <SummaryItem key={method}>
                            <SummaryLabel>{method}</SummaryLabel>
                            <SummaryValue>{formatEuros(total)}</SummaryValue>
                        </SummaryItem>
                    ))}
                </SummaryRow>

                {groupedByUsername.length === 0 ? (
                    <Typography>{t(TK.cashRegisterNoEntries)}</Typography>
                ) : (
                    <>
                        {renderGroupGrid(sharedGroups)}
                        {renderGroupGrid(personalGroups)}
                    </>
                )}
            </Container>

            {managedRegister && (
                <CashRegisterProcessModal
                    isOpen
                    register={{ username: managedRegister.username, cashRegisterName: managedRegister.cashRegisterName }}
                    displayName={managedRegister.displayName}
                    initialPaymentType={managedRegister.paymentType}
                    canApprove={canApprove}
                    sharedRegisterNames={sharedRegisterNames}
                    onAfterAction={loadEntries}
                    onClose={() => { setManagedRegister(null); loadEntries(); }}
                />
            )}
        </TitledContainer>
    );
};

export default CashRegister;
